<template>
    <div 
        class="main-container"
        role="tablist"
        aria-label="Subscription type selection"
        @keydown="handleKeydown"
    >
        <div
            class="card-container"
            :class="store.selectedUserType == SubscriptionType.INDIVIDUAL ? 'border-2 border-blue-700' : 'border border-zinc-300'"
            @click="selectUserType(SubscriptionType.INDIVIDUAL)"
            :tabindex="store.selectedUserType == SubscriptionType.INDIVIDUAL ? 0 : -1"
            role="tab"
            :aria-selected="store.selectedUserType == SubscriptionType.INDIVIDUAL"
            aria-label="Individual subscription type"
            ref="individualTab"
        >
            <Individual class="h-6 w-6" />
            <label for="individual" class="title-text">Individual</label>
        </div>

        <div
            class="card-container"
            :class="store.selectedUserType == SubscriptionType.COUPLES ? 'border-2 border-blue-700' : 'border border-zinc-300'"
            @click="selectUserType(SubscriptionType.COUPLES)"
            :tabindex="store.selectedUserType == SubscriptionType.COUPLES ? 0 : -1"
            role="tab"
            :aria-selected="store.selectedUserType == SubscriptionType.COUPLES"
            aria-label="Couples subscription type"
            ref="couplesTab"
        >
            <Couples class="h-6 w-6" />
            <label for="couples" class="title-text">Couples</label>
        </div>

        <div
            class="card-container"
            :class="store.selectedUserType == SubscriptionType.FAMILY ? 'border-2 border-blue-700' : 'border border-zinc-300'"
            @click="selectUserType(SubscriptionType.FAMILY)"
            :tabindex="store.selectedUserType == SubscriptionType.FAMILY ? 0 : -1"
            role="tab"
            :aria-selected="store.selectedUserType == SubscriptionType.FAMILY"
            aria-label="Family subscription type"
            ref="familyTab"
        >
            <Family class="h-6 w-6" />
            <label for="family" class="title-text">Family</label>
        </div>
    </div>
</template>

<script setup lang="ts">
import Couples from '~/assets/images/icons/couples.svg';
import Family from '~/assets/images/icons/family.svg';
import Individual from '~/assets/images/icons/individual.svg';
import { useSubscribeStore } from '~/stores/components/modals/subscription-modal';
import { SubscriptionType } from '~/types/licenses/licenses';

const store = useSubscribeStore();

const emit = defineEmits<{
    moveToNextSection: []
}>();

const individualTab = ref<HTMLElement | null>(null);
const couplesTab = ref<HTMLElement | null>(null);
const familyTab = ref<HTMLElement | null>(null);

// Tab order array
const tabs = [
    { type: SubscriptionType.INDIVIDUAL, ref: individualTab },
    { type: SubscriptionType.COUPLES, ref: couplesTab },
    { type: SubscriptionType.FAMILY, ref: familyTab }
];

const selectUserType = (type: SubscriptionType) => {
    store.selectedUserType = type;

    if (type === SubscriptionType.INDIVIDUAL) {
        store.licenseCount = 1;
    } else if (type === SubscriptionType.COUPLES) {
        store.licenseCount = 2;
    } else if (type === SubscriptionType.FAMILY) {
        store.licenseCount = 4;
    }
};

const handleKeydown = (event: KeyboardEvent) => {
    const currentIndex = tabs.findIndex(tab => tab.type === store.selectedUserType);
    let newIndex = currentIndex;

    switch (event.key) {
        case 'ArrowRight':
        case 'Right':
            event.preventDefault();
            // Move to next tab (circular)
            newIndex = (currentIndex + 1) % tabs.length;
            break;

        case 'ArrowLeft':
        case 'Left':
            event.preventDefault();
            // Move to previous tab (circular)
            newIndex = (currentIndex - 1 + tabs.length) % tabs.length;
            break;

        case 'Home':
            event.preventDefault();
            // Jump to first tab
            newIndex = 0;
            break;

        case 'End':
            event.preventDefault();
            // Jump to last tab
            newIndex = tabs.length - 1;
            break;

        case 'Enter':
            event.preventDefault()
            emit('moveToNextSection');
            return;

        case ' ':
            event.preventDefault();
            // Already selected, just trigger click
            return;

        default:
            return;
    }

    // Select and focus new tab
    if (newIndex !== currentIndex) {
        const newTab = tabs[newIndex];
        selectUserType(newTab.type);
        
        // Focus the new tab
        setTimeout(() => {
            newTab.ref.value?.focus();
        }, 0);
    }
};
</script>

<style lang="scss" scoped>
.main-container {
    @apply flex flex-row items-center sm:gap-6 gap-2 self-stretch;

    .card-container {
        @apply flex flex-col justify-center items-center gap-2 sm:p-6 px-2 py-3 flex-1;
        @apply rounded-lg bg-white shadow-sm cursor-pointer w-full;
    }

    .title-text {
        @apply text-zinc-950 font-inter text-sm font-semibold leading-4;
    }
}
</style>
